import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ vendors = [], onClose, onMessage, onBookNow }) => {
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  const maxComparisons = 3;

  const handleVendorSelect = (vendor) => {
    if (selectedVendors.find(v => v.id === vendor.id)) {
      setSelectedVendors(selectedVendors.filter(v => v.id !== vendor.id));
    } else if (selectedVendors.length < maxComparisons) {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  const handleCompare = () => {
    if (selectedVendors.length >= 2) {
      setShowComparison(true);
    }
  };

  const handleClearAll = () => {
    setSelectedVendors([]);
    setShowComparison(false);
  };

  const comparisonFeatures = [
    { key: 'rating', label: 'Rating', icon: 'Star' },
    { key: 'experienceYears', label: 'Experience', icon: 'Clock', suffix: ' years' },
    { key: 'startingPrice', label: 'Starting Price', icon: 'DollarSign', prefix: '$' },
    { key: 'location', label: 'Location', icon: 'MapPin' },
    { key: 'culturalSpecialization', label: 'Cultural Specialization', icon: 'Heart' },
    { key: 'languages', label: 'Languages', icon: 'Globe' },
    { key: 'services', label: 'Services', icon: 'Briefcase' },
    { key: 'isVerified', label: 'Verification', icon: 'Shield' },
    { key: 'isAvailable', label: 'Availability', icon: 'Calendar' }
  ];

  if (showComparison) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-strong max-w-6xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="font-heading font-bold text-2xl text-text-primary">
              Compare Vendors
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-100 transition-smooth flex items-center justify-center"
            >
              <Icon name="X" size={20} color="currentColor" />
            </button>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-heading font-semibold text-text-primary">
                    Features
                  </th>
                  {selectedVendors.map((vendor) => (
                    <th key={vendor.id} className="text-center p-4 min-w-64">
                      <div className="space-y-3">
                        <div className="w-16 h-16 mx-auto rounded-full overflow-hidden">
                          <Image
                            src={vendor.image}
                            alt={vendor.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-text-primary">
                            {vendor.name}
                          </h3>
                          <p className="text-sm text-secondary font-medium">
                            {vendor.culturalSpecialization}
                          </p>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, index) => (
                  <tr key={feature.key} className={index % 2 === 0 ? 'bg-surface-50' : 'bg-white'}>
                    <td className="p-4 font-medium text-text-primary">
                      <div className="flex items-center space-x-2">
                        <Icon name={feature.icon} size={16} color="#6B5B4F" />
                        <span>{feature.label}</span>
                      </div>
                    </td>
                    {selectedVendors.map((vendor) => (
                      <td key={vendor.id} className="p-4 text-center">
                        {feature.key === 'rating' && (
                          <div className="flex items-center justify-center space-x-1">
                            <Icon name="Star" size={16} color="#DAA520" />
                            <span className="font-medium">{vendor[feature.key]}</span>
                            <span className="text-sm text-text-muted">({vendor.reviewCount})</span>
                          </div>
                        )}
                        {feature.key === 'startingPrice' && (
                          <span className="font-heading font-bold text-primary">
                            {feature.prefix}{vendor[feature.key]?.toLocaleString()}
                          </span>
                        )}
                        {feature.key === 'experienceYears' && (
                          <span className="font-medium">
                            {vendor[feature.key]}{feature.suffix}
                          </span>
                        )}
                        {feature.key === 'languages' && (
                          <div className="text-sm">
                            {Array.isArray(vendor[feature.key]) 
                              ? vendor[feature.key].join(', ')
                              : vendor[feature.key]
                            }
                          </div>
                        )}
                        {feature.key === 'services' && (
                          <div className="text-sm">
                            {Array.isArray(vendor[feature.key])
                              ? `${vendor[feature.key].length} services`
                              : vendor[feature.key]
                            }
                          </div>
                        )}
                        {feature.key === 'isVerified' && (
                          <div className="flex items-center justify-center">
                            {vendor[feature.key] ? (
                              <div className="flex items-center space-x-1 text-success">
                                <Icon name="CheckCircle" size={16} color="currentColor" />
                                <span className="text-sm font-medium">Verified</span>
                              </div>
                            ) : (
                              <span className="text-sm text-text-muted">Not verified</span>
                            )}
                          </div>
                        )}
                        {feature.key === 'isAvailable' && (
                          <div className="flex items-center justify-center">
                            {vendor[feature.key] ? (
                              <div className="flex items-center space-x-1 text-success">
                                <Icon name="Calendar" size={16} color="currentColor" />
                                <span className="text-sm font-medium">Available</span>
                              </div>
                            ) : (
                              <span className="text-sm text-warning">Busy</span>
                            )}
                          </div>
                        )}
                        {!['rating', 'startingPrice', 'experienceYears', 'languages', 'services', 'isVerified', 'isAvailable'].includes(feature.key) && (
                          <span className="text-sm">{vendor[feature.key]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-border bg-surface-50">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handleClearAll}
                iconName="RotateCcw"
                iconSize={16}
              >
                Clear Selection
              </Button>
              
              <div className="flex items-center space-x-3">
                {selectedVendors.map((vendor) => (
                  <div key={vendor.id} className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onMessage?.(vendor.id)}
                      iconName="MessageCircle"
                      iconSize={14}
                    >
                      Message
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onBookNow?.(vendor.id)}
                      iconName="Calendar"
                      iconSize={14}
                    >
                      Book Now
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-t border-border shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="font-heading font-semibold text-text-primary">
              Compare Vendors
            </h3>
            <span className="text-sm text-text-secondary font-caption">
              Select up to {maxComparisons} vendors to compare
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <span className="text-sm text-text-secondary font-data">
              {selectedVendors.length}/{maxComparisons} selected
            </span>
            
            {selectedVendors.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                iconName="X"
                iconSize={14}
              >
                Clear
              </Button>
            )}
            
            <Button
              variant="primary"
              size="sm"
              onClick={handleCompare}
              disabled={selectedVendors.length < 2}
              iconName="BarChart3"
              iconSize={16}
            >
              Compare ({selectedVendors.length})
            </Button>
          </div>
        </div>

        {/* Selected Vendors */}
        {selectedVendors.length > 0 && (
          <div className="flex items-center space-x-4 mt-4 pt-4 border-t border-border-light">
            {selectedVendors.map((vendor) => (
              <div
                key={vendor.id}
                className="flex items-center space-x-2 bg-primary-50 rounded-lg px-3 py-2"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <Image
                    src={vendor.image}
                    alt={vendor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm font-medium text-primary">{vendor.name}</span>
                <button
                  onClick={() => handleVendorSelect(vendor)}
                  className="text-primary hover:text-primary-600 transition-colors"
                >
                  <Icon name="X" size={14} color="currentColor" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonTool;